import type { DrawingLayer } from "../drawings/DrawingLayer";
import type { Chart } from "../chart/Chart";
import { Icons } from "./icons";

const COLORS = ["#2962ff", "#26a69a", "#ef5350", "#ff9800", "#ab47bc", "#ffeb3b", "#ffffff", "#787b86"];

export class StylePopover {
  private el: HTMLElement;
  private layer: DrawingLayer;
  private chart: Chart;
  private built = false;

  constructor(layer: DrawingLayer, chart: Chart) {
    this.layer = layer;
    this.chart = chart;
    this.el = document.createElement("div");
    this.el.className = "style-pop";
    this.el.style.display = "none";
    document.body.appendChild(this.el);
  }

  private build() {
    const el = this.el;
    el.innerHTML = "";
    // color swatches
    for (const c of COLORS) {
      const sw = document.createElement("div");
      sw.className = "swatch";
      sw.style.background = c;
      sw.dataset.color = c;
      sw.addEventListener("click", () => this.layer.setStyleOfSelected({ color: c }));
      el.appendChild(sw);
    }
    el.appendChild(this.vsep());

    // width
    const range = document.createElement("input");
    range.type = "range";
    range.min = "1";
    range.max = "6";
    range.step = "1";
    range.title = "Line width";
    range.addEventListener("input", () => this.layer.setStyleOfSelected({ width: parseInt(range.value, 10) }));
    (this as any)._range = range;
    el.appendChild(range);

    // dash toggle
    const dash = this.spBtn("┈", "Dashed", () => {
      const sel = this.layer.selected;
      if (!sel) return;
      this.layer.setStyleOfSelected({ dash: sel.style.dash ? null : [6, 4] });
    });
    (this as any)._dash = dash;
    el.appendChild(dash);

    // fill toggle
    const fill = this.spBtn("▤", "Fill", () => {
      const sel = this.layer.selected;
      if (!sel) return;
      this.layer.setStyleOfSelected({ fillAlpha: sel.style.fillAlpha > 0 ? 0 : 0.15 });
    });
    (this as any)._fill = fill;
    el.appendChild(fill);

    el.appendChild(this.vsep());

    // edit text (only for text)
    const editText = this.spBtn("T", "Edit text", () => this.layer.editSelectedText());
    (this as any)._editText = editText;
    el.appendChild(editText);

    // delete
    const del = document.createElement("button");
    del.className = "sp-btn";
    del.title = "Delete (Del)";
    del.innerHTML = Icons.trash;
    del.addEventListener("click", () => this.layer.deleteSelected());
    el.appendChild(del);

    this.built = true;
  }

  private vsep() {
    const s = document.createElement("div");
    s.className = "sp-sep";
    return s;
  }
  private spBtn(label: string, title: string, onClick: () => void) {
    const b = document.createElement("button");
    b.className = "sp-btn";
    b.title = title;
    b.textContent = label;
    b.addEventListener("click", onClick);
    return b;
  }

  update() {
    const sel = this.layer.selected;
    if (!sel || this.layer.activeTool !== "cursor") {
      this.el.style.display = "none";
      return;
    }
    if (!this.built) this.build();

    // reflect state
    const range = (this as any)._range as HTMLInputElement;
    range.value = String(sel.style.width);
    ((this as any)._dash as HTMLElement).classList.toggle("active", !!sel.style.dash);
    ((this as any)._fill as HTMLElement).classList.toggle("active", sel.style.fillAlpha > 0);
    ((this as any)._editText as HTMLElement).style.display = sel.type === "text" ? "" : "none";
    for (const sw of Array.from(this.el.querySelectorAll<HTMLElement>(".swatch"))) {
      sw.classList.toggle("sel", sw.dataset.color === sel.style.color);
    }

    // position above the topmost handle
    const handles = sel.handles(this.chart);
    if (!handles.length) {
      this.el.style.display = "none";
      return;
    }
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity;
    for (const h of handles) {
      minX = Math.min(minX, h.x);
      maxX = Math.max(maxX, h.x);
      minY = Math.min(minY, h.y);
    }
    const rect = this.chart.container.getBoundingClientRect();
    this.el.style.display = "flex";
    const cx = rect.left + (minX + maxX) / 2;
    let top = rect.top + minY - 46;
    if (minY < 60) top = rect.top + minY + 30;
    this.el.style.left = Math.max(rect.left + 4, cx - this.el.offsetWidth / 2) + "px";
    this.el.style.top = top + "px";
  }
}
